/**
 * Extractieprofielen — de vijf AI-modellen die de applicatie rijk is.
 *
 * Een profiel is één combinatie van (system prompt + JSON-schema) die één soort
 * transcript omzet naar één slice van een boring. In de UI heten deze gewoon
 * "modellen"; in code heten ze `ExtractionProfile` omdat `extractionModel` in de
 * AI-config al de LLM-id is (bijv. `gpt-4o`) en die twee niet door elkaar mogen lopen.
 *
 * Een profiel is (domein × classificatiestandaard):
 * - het domein bepaalt WELK deel van de boring wordt opgebouwd (lagen/peilbuis/
 *   watermonster/afwerking) en dus welk JSON-schema geldt;
 * - de standaard bepaalt hoe grondlagen geclassificeerd worden (NEN 5104 / NEN 6693)
 *   en is daarmee alleen betekenisvol voor het lagen-domein.
 *
 * Elk profiel heeft een eigen promptversie-historie en een eigen validatie-testset:
 * één sample hoort bij precies één profiel — dezelfde audio draaien we niet op
 * meerdere modellen.
 */

import type { SoilStandard } from './standards.js';

/** Het deel van de boring dat een opname beschrijft. */
export type ExtractionDomain = 'lagen' | 'peilbuis' | 'watermonster' | 'afwerking';

export type ExtractionProfileId =
  | 'nen5104'
  | 'nen6693'
  | 'iso14688'
  | 'peilbuis'
  | 'watermonster'
  | 'afwerking';

export interface ExtractionProfileDef {
  id: ExtractionProfileId;
  /** Volledige weergavenaam (tabbladen, koppen). */
  label: string;
  /** Korte weergave (badges, smalle kolommen). */
  short: string;
  domain: ExtractionDomain;
  /** Alleen betekenisvol voor het lagen-domein; domeinmodellen zijn standaard-agnostisch. */
  soilStandard: SoilStandard;
  /** Eén regel uitleg — subtitel van de subpagina. */
  description: string;
}

/** Volgorde = volgorde van de tabbladen op /admin/validation. */
export const EXTRACTION_PROFILES: readonly ExtractionProfileDef[] = [
  {
    id: 'nen5104',
    label: 'Lagen NEN 5104',
    short: 'NEN 5104',
    domain: 'lagen',
    soilStandard: 'nen5104',
    description: 'Grondlagen volgens de oude NEN 5104-systematiek.',
  },
  {
    id: 'nen6693',
    label: 'Lagen NEN 6693',
    short: 'NEN 6693',
    domain: 'lagen',
    soilStandard: 'nen6693',
    description: 'Grondlagen volgens de verplichte NEN 6693-systematiek.',
  },
  {
    id: 'iso14688',
    label: 'Lagen NEN-EN-ISO 14688',
    short: 'ISO 14688',
    domain: 'lagen',
    soilStandard: 'iso14688',
    description: 'Grondlagen volgens NEN-EN-ISO 14688, met NEN 6693 als basis (zoals TerraIndex hem aanbiedt).',
  },
  {
    id: 'peilbuis',
    label: 'Peilbuis',
    short: 'Peilbuis',
    domain: 'peilbuis',
    soilStandard: 'nen6693',
    description: 'Peilbuisgegevens: filterstelling, materiaal, doorloop en referentievlak.',
  },
  {
    id: 'watermonster',
    label: 'Watermonster',
    short: 'Watermonster',
    domain: 'watermonster',
    soilStandard: 'nen6693',
    description: 'Watermonsters: bemonsteringsdiepte, veldmetingen en potjes.',
  },
  {
    id: 'afwerking',
    label: 'Afwerking',
    short: 'Afwerking',
    domain: 'afwerking',
    soilStandard: 'nen6693',
    description: 'Afwerking van het boorgat en de bovenafwerking van een peilbuis.',
  },
] as const;

/** De subpagina die /admin/validation zonder model opent. */
export const DEFAULT_EXTRACTION_PROFILE: ExtractionProfileId = 'nen5104';

export const EXTRACTION_PROFILE_IDS: readonly ExtractionProfileId[] =
  EXTRACTION_PROFILES.map((p) => p.id);

/** Normaliseer een opgeslagen/ruwe waarde naar een geldig profiel-id (fallback = default). */
export function normalizeExtractionProfile(
  value: string | null | undefined,
): ExtractionProfileId {
  return EXTRACTION_PROFILE_IDS.includes(value as ExtractionProfileId)
    ? (value as ExtractionProfileId)
    : DEFAULT_EXTRACTION_PROFILE;
}

export function getExtractionProfile(
  value: string | null | undefined,
): ExtractionProfileDef {
  const id = normalizeExtractionProfile(value);
  return EXTRACTION_PROFILES.find((p) => p.id === id)!;
}

/**
 * Het profiel dat bij een opname hoort. Een lagen-opname valt uiteen in twee
 * profielen (per standaard); de domeinopnamen hebben er precies één.
 */
export function extractionProfileFor(
  domain: string | null | undefined,
  soilStandard: string | null | undefined,
): ExtractionProfileId {
  if (domain === 'peilbuis' || domain === 'watermonster' || domain === 'afwerking') return domain;
  if (soilStandard === 'nen6693' || soilStandard === 'iso14688') return soilStandard;
  return 'nen5104';
}
